import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Search, ChevronDown, ArrowRight } from 'lucide-react';
import { CLINIC, IMAGES, DISORDERS } from '../mock';
import { PageHero, Reveal } from '../components/Shared';

const DETAILS = {
  'rheumatoid-arthritis': { symptoms: ['Persistent joint pain, worse in the morning', 'Morning stiffness lasting >30 minutes', 'Symmetrical joint swelling', 'Fatigue and low-grade fever'], treatment: 'DMARDs (methotrexate, hydroxychloroquine), biologics (TNF inhibitors), NSAIDs and physiotherapy.' },
  'ankylosing-spondylitis': { symptoms: ['Lower back pain and stiffness', 'Symptoms improve with movement', 'Reduced spinal mobility', 'Eye inflammation (uveitis)'], treatment: 'NSAIDs, biologics (TNF & IL-17 inhibitors), targeted physiotherapy and posture training.' },
  'lupus-sle': { symptoms: ['Butterfly-shaped facial rash', 'Joint pain and swelling', 'Persistent fatigue', 'Kidney involvement (lupus nephritis)'], treatment: 'Hydroxychloroquine, corticosteroids, immunosuppressants (MMF), Belimumab and Rituximab.' },
  'psoriatic-arthritis': { symptoms: ['Swollen fingers/toes (dactylitis)', 'Psoriasis skin plaques', 'Nail pitting', 'Enthesitis (heel/tendon pain)'], treatment: 'DMARDs, biologics (TNF, IL-17, IL-23 inhibitors) and skin-joint combined care.' },
  'polymyalgia-rheumatica': { symptoms: ['Shoulder, hip and neck stiffness', 'Worse in the morning', 'Fatigue and low-grade fever', 'Common after age 50'], treatment: 'Low-dose corticosteroids (prednisone), tapered gradually, with bone protection.' },
  'sjogrens-syndrome': { symptoms: ['Dry eyes (gritty sensation)', 'Dry mouth, difficulty swallowing', 'Joint pain', 'Fatigue'], treatment: 'Artificial tears, saliva stimulants, anti-inflammatory therapy and multidisciplinary care.' },
  'scleroderma': { symptoms: ['Thickened, hardened skin', "Raynaud's phenomenon", 'Acid reflux / difficulty swallowing', 'Shortness of breath'], treatment: 'Immunosuppressants, antifibrotics, Raynaud medications and organ-specific care.' },
  'vasculitis': { symptoms: ['Skin rashes / ulcers', 'Nerve tingling or weakness', 'Fatigue and fever', 'Organ-specific symptoms'], treatment: 'Corticosteroids, immunosuppressants and biologics such as Rituximab under close monitoring.' },
  'gout': { symptoms: ['Sudden severe joint pain (often big toe)', 'Redness and swelling', 'Warmth over the joint', 'Recurrent flares'], treatment: 'NSAIDs/colchicine for flares, urate-lowering therapy (allopurinol/febuxostat) long-term.' },
};

export default function DisordersWeTreat() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(null);
  const list = DISORDERS.filter((d) => d.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div>
      <PageHero eyebrow="Conditions" title="Arthritis & autoimmune diseases we treat" subtitle="From common forms of arthritis to rare autoimmune syndromes — accurate diagnosis and modern, evidence-based treatment." image={IMAGES.arthritisHand} />

      <section className="py-16">
        <div className="container-x">
          <div className="relative max-w-md mb-10">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search conditions..." className="w-full pl-11 pr-4 py-3 rounded-full border border-teal-100 bg-white outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((d, i) => {
              const det = DETAILS[d.id];
              const isOpen = open === d.id;
              return (
                <Reveal key={d.id} delay={(i % 3) * 80}>
                  <div className="card-lift bg-white rounded-2xl overflow-hidden border border-teal-50 h-full flex flex-col">
                    <div className="h-40 overflow-hidden">
                      <img src={IMAGES[d.image]} alt={d.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="text-lg text-teal-900 mb-2" style={{ fontWeight: 600 }}>{d.name}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{d.short}</p>
                      <button onClick={() => setOpen(isOpen ? null : d.id)} className="flex items-center justify-between w-full mt-4 text-teal-700 text-sm font-medium">
                        {isOpen ? 'Hide details' : 'Symptoms & treatment'}
                        <ChevronDown size={17} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <div className={`grid transition-all duration-300 ${isOpen ? 'grid-rows-[1fr] opacity-100 mt-4' : 'grid-rows-[0fr] opacity-0'}`}>
                        <div className="overflow-hidden">
                          <div className="text-xs font-semibold text-teal-800 uppercase tracking-wide mb-2">Common Symptoms</div>
                          <ul className="space-y-1.5 mb-4">
                            {det.symptoms.map((s) => <li key={s} className="flex gap-2 text-sm text-slate-600"><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-coral shrink-0" style={{ background: 'var(--coral)' }} /> {s}</li>)}
                          </ul>
                          <div className="text-xs font-semibold text-teal-800 uppercase tracking-wide mb-1">Treatment</div>
                          <p className="text-sm text-slate-600 leading-relaxed">{det.treatment}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          {list.length === 0 && <p className="text-center text-slate-500 py-10">No conditions match "{query}".</p>}
        </div>
      </section>

      <section className="mint-section">
        <div className="container-x py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl text-teal-900 text-balance" style={{ fontWeight: 600 }}>Not sure what's causing your joint pain?</h2>
            <p className="text-slate-600 mt-2">Early diagnosis makes all the difference. Book a detailed assessment today.</p>
          </div>
          <div className="flex gap-3 shrink-0">
            <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"><Phone size={18} /> Call Now</a>
            <Link to="/second-opinion" className="btn-outline inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold bg-white">Second Opinion <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
